import React from 'react';
import PrintLayout from '../PrintLayout';
import { extractTaggedTexts } from '../../../utils/shortcodes';

const ELEMENTS = [
  { pos: '/r', neg: '/r-', title: 'Clear Roles' },
  { pos: '/u', neg: '/u-', title: 'Helpful Rules' },
  { pos: '/p', neg: '/p-', title: 'Functional Parts' },
  { pos: '/t', neg: '/t-', title: 'Effective Tools' },
  { pos: '/a', neg: '/a-', title: 'Decisions/Actions' },
  { pos: '/m', neg: '/m-', title: 'Cascading Messages' },
];

function collectNotes(entries, pos, neg) {
  const notes = [];
  entries.forEach(e => {
    extractTaggedTexts(e.text || '', pos).forEach(text => notes.push({ text, positive: true }));
    extractTaggedTexts(e.text || '', neg).forEach(text => notes.push({ text, positive: false }));
  });
  return notes;
}

// Notes area is a fixed height (see ElementCard) so cards stay uniform regardless of
// note count. Rather than let CSS overflow clip a note mid-line, measure each note's
// actual wrapped line count and only include notes that fit entirely — a note that
// would spill past the bottom is dropped whole rather than shown cut off.
const NOTES_BUDGET_PX = 66;
const NOTE_FONT_PX = 9.5;
const NOTE_LINE_HEIGHT_PX = NOTE_FONT_PX * 1.45;
const NOTE_ROW_GAP_PX = 3; // marginBottom per note row
const NOTE_TEXT_WIDTH_PX = 518; // available text width inside a card at this page's fixed 1280px layout

let measureCanvas;
function textLineCount(text, maxWidthPx, fontPx = NOTE_FONT_PX) {
  if (!text) return 0;
  if (!measureCanvas) measureCanvas = document.createElement('canvas');
  const ctx = measureCanvas.getContext('2d');
  ctx.font = `${fontPx}px Arial, sans-serif`;
  let lines = 1;
  let widthUsed = 0;
  text.split(' ').forEach((word, i) => {
    const piece = (i > 0 ? ' ' : '') + word;
    const pieceWidth = ctx.measureText(piece).width;
    if (widthUsed + pieceWidth > maxWidthPx && widthUsed > 0) {
      lines += 1;
      widthUsed = ctx.measureText(word).width;
    } else {
      widthUsed += pieceWidth;
    }
  });
  return lines;
}

function fitNotes(notes) {
  let used = 0;
  const fitted = [];
  for (const note of notes) {
    const h = textLineCount(note.text, NOTE_TEXT_WIDTH_PX) * NOTE_LINE_HEIGHT_PX + NOTE_ROW_GAP_PX;
    if (used + h > NOTES_BUDGET_PX) break;
    used += h;
    fitted.push(note);
  }
  return fitted;
}

function ElementCard({ n, title, positive, total, notes }) {
  const shownNotes = notes.length ? fitNotes(notes) : notes;
  return (
    <div style={{
      background: 'white', border: '1px solid rgba(42,61,91,0.1)', borderRadius: 10,
      padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 6, overflow: 'hidden',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <div style={{
            width: 20, height: 20, borderRadius: 6, background: 'rgba(42,61,91,0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, color: '#2a3d5b', fontSize: 9,
          }}>
            {n}
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#2a3d5b' }}>{title}</div>
        </div>
        <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'flex-end', marginLeft: 10 }}>
          {total === 0
            ? <div style={{ fontSize: 9.5, color: '#9aa2ab', fontStyle: 'italic' }}>Not tagged this meeting</div>
            : Array.from({ length: total }).map((_, i) => (
              <div key={i} style={{
                width: 16, height: 12, borderRadius: 2,
                background: i < positive ? '#5f7942' : 'transparent',
                border: i < positive ? 'none' : '1.5px dashed #922b3f',
              }} />
            ))}
        </div>
      </div>

      <div style={{ height: NOTES_BUDGET_PX, overflow: 'hidden' }}>
        {notes.length === 0
          ? <div style={{ fontSize: 9.5, color: '#9aa2ab', fontStyle: 'italic' }}>No notes recorded</div>
          : shownNotes.map((note, i) => (
            <div key={i} style={{ display: 'flex', gap: 5, marginBottom: 3 }}>
              <div style={{
                width: 6, height: 6, borderRadius: '50%', marginTop: 4, flexShrink: 0,
                background: note.positive ? '#5f7942' : '#922b3f',
              }} />
              <div style={{ fontSize: NOTE_FONT_PX, color: '#4a5568', lineHeight: 1.45 }}>{note.text}</div>
            </div>
          ))}
      </div>

      <div style={{ textAlign: 'right', fontSize: 10.5, fontWeight: 700, color: '#6b757e' }}>{positive}/{total}</div>
    </div>
  );
}

export default function PrintProcessTally({ pageNumber, reportData, entries = [] }) {
  const codes = reportData.processCodes || {};

  return (
    <PrintLayout pageNumber={pageNumber}>
      <div style={{ position: 'absolute', top: 0, bottom: 68, left: 0, right: 0, padding: '5% 6%', background: '#f5f4f0', display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 24, fontWeight: 700, color: '#1e2e45', marginBottom: 4 }}>
          Simple Process: Hits and Misses
        </div>
        <div style={{ fontSize: 11, color: '#6b757e', marginBottom: 16 }}>
          Green marks productive moments in a meeting. Red flags mark missed opportunities.
        </div>
        <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
          {ELEMENTS.map((el, i) => {
            const c = codes[el.pos] || {};
            return (
              <ElementCard
                key={el.pos}
                n={i + 1}
                title={el.title}
                positive={c.positive || 0}
                total={c.total || 0}
                notes={collectNotes(entries, el.pos, el.neg)}
              />
            );
          })}
        </div>
      </div>
    </PrintLayout>
  );
}
